import * as React from 'react';
import ChatbotPanel, { ChatbotPanelHandle } from './ChatbotPanel';
import { ChatHistorySidebar } from './ChatHistorySidebar';
import { AnalyseDocPage } from './AnalyseDocPage';
import { MesClausesPage } from './MesClausesPage';
import { MesContratsPage } from './MesContratsPage';
import { EnrichirPage } from './EnrichirPage';
import { NotificationsPage } from './NotificationsPage';
import { CompanySelectionPage } from './CompanySelectionPage';
import { useAppContext } from '../context/AppContext';
import { useImpersonation } from '../context/ImpersonationContext';
import * as api from '../services/apiClient';
import { logger } from '../utils/logger';
import '../styles/MainEditorView.css';

export const MainEditorView: React.FC = () => {
  const { activePage } = useAppContext();
  const { activeAlias } = useImpersonation();

  // Chat history state - managed at MainEditorView level for sidebar
  const [conversations, setConversations] = React.useState<api.Conversation[]>([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isLoadingConversations, setIsLoadingConversations] = React.useState(false);
  const [currentConversationId, setCurrentConversationId] = React.useState<string | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);

  // Ref to ChatbotPanel handlers
  const chatbotPanelRef = React.useRef<ChatbotPanelHandle | null>(null);

  // Load conversations
  const loadConversations = React.useCallback(async () => {
    setIsLoadingConversations(true);
    try {
      const response = await api.listConversations(1, 100);
      if (response.success && response.data) {
        setConversations(response.data.items);
        // Sync current conversation ID from the panel ref only if not already set
        // (to avoid overwriting user selection)
        setCurrentConversationId(prev =>
          prev ?? (chatbotPanelRef.current?.conversationId ?? null)
        );
      } else {
        logger.error('Error loading conversations:', response.error);
        setConversations([]);
      }
    } catch (error) {
      logger.error('Error loading conversations:', error);
      setConversations([]);
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  // Reset conversations when impersonation changes
  React.useEffect(() => {
    setConversations([]);
    setSearchQuery('');
    setCurrentConversationId(null);
  }, [activeAlias]);

  // Handlers to pass to sidebar
  const handleSelectConversation = React.useCallback((id: string, name: string) => {
    chatbotPanelRef.current?.selectConversation(id, name);
    setCurrentConversationId(id);
  }, []);

  const handleNewConversation = React.useCallback(() => {
    chatbotPanelRef.current?.handleNewConversation();
    setCurrentConversationId(null);
  }, []);

  const handleDeleteConversation = React.useCallback(async (id: string) => {
    try {
      const response = await api.deleteConversation(id);
      if (!response.success) {
        throw new Error(response.error);
      }
      // Reload conversations after deletion
      await loadConversations();
      // If it was the active conversation, start a new one
      if (id === currentConversationId) {
        chatbotPanelRef.current?.handleNewConversation();
        setCurrentConversationId(null);
      }
    } catch (error: any) {
      logger.error('Error deleting conversation:', error);
    }
  }, [loadConversations, currentConversationId]);

  // Toutes les pages sont gardées montées pour préserver leur état
  // On utilise CSS pour afficher/masquer selon la page active
  return (
    <div className="main-editor-view-container">
      {/* Page Chat */}
      <div className={`page-container ${activePage === 'chat' ? 'page-active' : 'page-hidden'}`}>
        <div className="chat-page-layout">
          <ChatHistorySidebar
            conversations={conversations}
            currentConversationId={currentConversationId}
            onSelectConversation={handleSelectConversation}
            onNewConversation={handleNewConversation}
            onDeleteConversation={handleDeleteConversation}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            isLoading={isLoadingConversations}
            onLoadConversations={loadConversations}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(prev => !prev)}
          />
          <div className="chat-main-area">
            <ChatbotPanel
              ref={chatbotPanelRef}
              onConversationChange={loadConversations}
            />
          </div>
        </div>
      </div>

      {/* Page Analyse de doc */}
      <div className={`page-container ${activePage === 'analyse-doc' ? 'page-active' : 'page-hidden'}`}>
        <AnalyseDocPage />
      </div>

      {/* Page Mes Clauses */}
      <div className={`page-container ${activePage === 'page3' ? 'page-active' : 'page-hidden'}`}>
        <MesClausesPage />
      </div>

      {/* Page Mes Contrats */}
      <div className={`page-container ${activePage === 'page4' ? 'page-active' : 'page-hidden'}`}>
        <MesContratsPage />
      </div>

      {/* Page Enrichir */}
      <div className={`page-container ${activePage === 'enrichir' ? 'page-active' : 'page-hidden'}`}>
        <EnrichirPage />
      </div>

      {/* Page Notifications */}
      <div className={`page-container ${activePage === 'notifications' ? 'page-active' : 'page-hidden'}`}>
        <NotificationsPage />
      </div>

      {/* Page Company Selection (hidden from nav, admin only) */}
      <div className={`page-container ${activePage === 'company-selection' ? 'page-active' : 'page-hidden'}`}>
        <CompanySelectionPage />
      </div>
    </div>
  );
};
